import { useCallback, useReducer } from 'react';
import { useQuery } from 'react-query';

import { OrderDetails, Product } from '@savgroup-front-common/types';
import { OwnerService } from '@/api';

import { adaptProductsEligibility } from './adapters/adaptProductsEligibility';
import ChooseProductPageContentReducer, {
  ChooseProductPageContentReducerAction,
  ChooseProductPageContentReducerState,
  initDiagnosticTreeState,
} from './ChooseProductPageContent.reducer';
import { PRODUCT_STATE } from './NewChooseProductPage.types';

export const GET_ORDER_DETAIL = 'getOrderDetail';

interface UseChooseProductArgs {
  orderId: string;
  issue?: {
    id: string;
    winningWarrantyTypeId: string;
    name: string;
    didactic: string;
    fromProductWarranty: boolean;
    displayOrder: number;
    ownerProductIds?: string[];
    uniqueName: string;
    key: string;
    iconKey: string;
  };
}

interface UseChooseProductReturnValues {
  orderDetail: OrderDetails | undefined;
  isLoading: boolean;
  eligibleProducts: Product[];
  notEligibleProducts: Product[];
  notEligibleProductsFollowUpClaim: Product[];
  isGenericOrder?: boolean;
}

const useChooseProductContent = ({
  orderId,
  issue,
}: UseChooseProductArgs): UseChooseProductReturnValues => {
  const [chooseProductContentState, chooseProductContentStateDispatch] =
    useReducer<
      ChooseProductPageContentReducerState,
      [ChooseProductPageContentReducerAction]
    >(ChooseProductPageContentReducer, initDiagnosticTreeState());

  const handleProducts = useCallback(
    (orderDetail: OrderDetails) => {
      const {
        eligibleProducts,
        notEligibleProducts,
        notEligibleProductsFollowUpClaim,
      } = adaptProductsEligibility({
        products: orderDetail.products,
        eligibleOwnerProductIds: issue?.ownerProductIds,
      });

      chooseProductContentStateDispatch({
        type: PRODUCT_STATE.UPDATE,
        payload: {
          eligibleProducts,
          notEligibleProducts,
          notEligibleProductsFollowUpClaim,
        },
      });
    },
    [issue],
  );

  const { data: orderDetail, isLoading } = useQuery(
    [GET_ORDER_DETAIL, { orderId }],
    async () => {
      const response = await OwnerService.getOrderDetailsQuery({
        orderId,
      });

      if (response.failure) {
        return undefined;
      }

      return response.value;
    },
    {
      enabled: !!orderId,
      onSuccess: (orderDetail) => orderDetail && handleProducts(orderDetail),
    },
  );

  return {
    orderDetail,
    isLoading,
    eligibleProducts: chooseProductContentState?.eligibleProducts || [],
    notEligibleProducts: chooseProductContentState?.notEligibleProducts || [],
    notEligibleProductsFollowUpClaim:
      chooseProductContentState?.notEligibleProductsFollowUpClaim || [],
    isGenericOrder: orderDetail?.isGenericOrder || false,
  };
};

export default useChooseProductContent;
